import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Shield, FileText, AlertTriangle, CheckCircle, Lock, Unlock, BarChart2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '@/services/api';
import { errorLog } from '@/utils/logger';

interface PayoutSettings {
  autoApprovalEnabled: boolean;
  autoApprovalThreshold: number;
  bulkApprovalThreshold: number;
  bulkMaxAutoCount: number;
  requireApprovalForNewWallets: boolean;
  newWalletThresholdDays: number;
  emergencyMode: boolean;
  adminDebugEnabled: boolean;
}

interface SignatureRequest {
  id: string;
  payoutRequestId: string;
  walletAddress: string;
  amount: number;
  status: string;
  createdAt: string;
  user?: {
    fullName: string;
    email: string;
  };
}

interface ContractStatus {
  isPaused: boolean;
  address: string;
}

interface MinterUsage {
  dailyLimit: number;
  perTxLimit: number;
  usedToday: number;
  remainingToday: number;
  lastResetDate: string;
}

interface PayoutStats {
  today:     { count: number; totalHep: number; auto: number; manual: number; rejected: number };
  thisWeek:  { count: number; totalHep: number; auto: number; manual: number };
  thisMonth: { count: number; totalHep: number; auto: number; manual: number; uniqueUsers: number };
  allTime:   { count: number; totalHep: number; avgPayoutHep: number; maxPayoutHep: number };
  pending:   { count: number; totalHep: number };
}

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'thresholds' | 'risk' | 'signatures' | 'debug' | 'stats'>('thresholds');
  const [settings, setSettings] = useState<PayoutSettings | null>(null);
  const [signatureRequests, setSignatureRequests] = useState<SignatureRequest[]>([]);
  const [contractStatus, setContractStatus] = useState<ContractStatus | null>(null);
  const [minterUsage, setMinterUsage] = useState<MinterUsage | null>(null);
  const [payoutStats, setPayoutStats] = useState<PayoutStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [autoApprovalAvailable, setAutoApprovalAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchSignatureRequests();
    fetchContractStatus();
    fetchMinterUsage();
    fetchAutoApprovalStatus();
    fetchPayoutStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await apiClient.settings.get();
      if (res.success) {
        setSettings(res.data);
      }
    } catch (error: any) {
      errorLog('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSignatureRequests = async () => {
    try {
      const res = await apiClient.settings.getPendingSignatures();
      if (res.success) {
        setSignatureRequests(res.data || []);
      }
    } catch (error: any) {
      errorLog('Failed to fetch signature requests:', error);
    }
  };

  const fetchContractStatus = async () => {
    try {
      const res = await apiClient.settings.getContractStatus();
      if (res.success) {
        setContractStatus(res.data);
      }
    } catch (error: any) {
      errorLog('Failed to fetch contract status:', error);
    }
  };

  const fetchMinterUsage = async () => {
    try {
      const res = await apiClient.settings.getMinterUsage();
      if (res.success) {
        setMinterUsage(res.data);
      }
    } catch (error: any) {
      errorLog('Failed to fetch minter usage:', error);
    }
  };

  const fetchAutoApprovalStatus = async () => {
    try {
      const res = await apiClient.settings.getAutoApprovalStatus();
      if (res.success) setAutoApprovalAvailable(res.data.available);
    } catch {
      setAutoApprovalAvailable(false);
    }
  };

  const fetchPayoutStats = async () => {
    try {
      const res = await apiClient.settings.getPayoutStats();
      if (res.success) setPayoutStats(res.data);
    } catch { /* silent */ }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      const res = await apiClient.settings.update(settings);
      if (res.success) {
        toast.success('Settings saved successfully');
      }
    } catch (error: any) {
      errorLog('Failed to save settings:', error);
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEmergencyMode = async () => {
    if (!settings) return;

    const newValue = !settings.emergencyMode;
    try {
      const res = await apiClient.settings.toggleEmergencyMode(newValue);
      if (res.success) {
        setSettings({ ...settings, emergencyMode: newValue });
        toast.success(`Emergency mode ${newValue ? 'enabled' : 'disabled'}`);
      }
    } catch (error: any) {
      errorLog('Failed to toggle emergency mode:', error);
      toast.error('Failed to toggle emergency mode');
    }
  };

  const handlePauseContract = async () => {
    if (!window.confirm('Are you sure you want to pause the contract? This will disable all minting operations.')) {
      return;
    }

    try {
      const res = await apiClient.settings.pauseContract();
      if (res.success) {
        toast.success('Contract paused successfully');
        fetchContractStatus();
      }
    } catch (error: any) {
      errorLog('Failed to pause contract:', error);
      toast.error('Failed to pause contract');
    }
  };

  if (isLoading || !settings) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Settings className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-spin" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin')}
          className="p-2 text-gray-500 hover:text-gray-700 touch-manipulation"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-4xl font-bold text-white">Payout Settings</h1>
          <p className="text-lg text-white mt-1">Configure hybrid approval system</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap">
            <button
              onClick={() => setActiveTab('thresholds')}
              className={`px-4 sm:px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'thresholds'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Approval Thresholds
            </button>
            <button
              onClick={() => setActiveTab('risk')}
              className={`px-4 sm:px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'risk'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Risk Controls
            </button>
            <button
              onClick={() => setActiveTab('signatures')}
              className={`px-4 sm:px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'signatures'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Pending Signatures ({signatureRequests.length})
            </button>
            <button
              onClick={() => setActiveTab('debug')}
              className={`px-4 sm:px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'debug'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Debug
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-4 sm:px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'stats'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Stats
            </button>
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {/* Approval Thresholds Tab */}
          {activeTab === 'thresholds' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="text-blue-600" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">Auto-Approval</p>
                      <p className="text-sm text-gray-600">Automatically approve small payouts</p>
                    </div>
                  </div>
                  <label className={`relative inline-flex items-center ${autoApprovalAvailable === false ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={autoApprovalAvailable === false ? false : settings.autoApprovalEnabled}
                      disabled={autoApprovalAvailable === false}
                      onChange={(e) => {
                        if (e.target.checked && autoApprovalAvailable === false) {
                          toast.error('Cannot enable: LIMITED_MINTER_PRIVATE_KEY is not configured on the backend');
                          return;
                        }
                        setSettings({ ...settings, autoApprovalEnabled: e.target.checked });
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                {autoApprovalAvailable === false && (
                  <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                    <AlertTriangle className="text-yellow-600 shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-yellow-800">
                      Auto-approval is unavailable. Configure <code className="bg-yellow-100 px-1 rounded">LIMITED_MINTER_PRIVATE_KEY</code> in the backend environment to enable this feature.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auto-Approval Threshold (HEP)
                  </label>
                  <input
                    type="number"
                    value={settings.autoApprovalThreshold}
                    onChange={(e) => setSettings({ ...settings, autoApprovalThreshold: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    step="0.01"
                    min="0"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Payouts below this amount will be auto-approved
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bulk Approval Threshold (HEP)
                  </label>
                  <input
                    type="number"
                    value={settings.bulkApprovalThreshold}
                    onChange={(e) => setSettings({ ...settings, bulkApprovalThreshold: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    step="0.01"
                    min="0"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Maximum amount for bulk approval operations
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <button
                  onClick={handleSaveSettings}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {/* Risk Controls Tab */}
          {activeTab === 'risk' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="text-yellow-600" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">New Wallet Protection</p>
                    <p className="text-sm text-gray-600">Require approval for new wallets</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.requireApprovalForNewWallets}
                    onChange={(e) => setSettings({ ...settings, requireApprovalForNewWallets: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                </label>
              </div>

              {settings.requireApprovalForNewWallets && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wallet Age Threshold (days)
                  </label>
                  <input
                    type="number"
                    value={settings.newWalletThresholdDays}
                    onChange={(e) => setSettings({ ...settings, newWalletThresholdDays: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    min="0"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Wallets younger than this require manual approval
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="text-red-600" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Emergency Mode</p>
                    <p className="text-sm text-gray-600">Escalate all payouts to manual approval</p>
                  </div>
                </div>
                <button
                  onClick={handleToggleEmergencyMode}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    settings.emergencyMode
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {settings.emergencyMode ? 'Disable' : 'Enable'}
                </button>
              </div>

              {contractStatus && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {contractStatus.isPaused ? (
                        <Lock className="text-red-600" size={20} />
                      ) : (
                        <Unlock className="text-green-600" size={20} />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">Contract Status</p>
                        <p className="text-sm text-gray-600">
                          {contractStatus.isPaused ? 'Paused - all minting disabled' : 'Active'}
                        </p>
                      </div>
                    </div>
                    {!contractStatus.isPaused && (
                      <button
                        onClick={handlePauseContract}
                        disabled={autoApprovalAvailable === false}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Emergency Pause
                      </button>
                    )}
                  </div>
                  {!contractStatus.isPaused && autoApprovalAvailable === false && (
                    <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                      <AlertTriangle className="text-yellow-600 shrink-0 mt-0.5" size={16} />
                      <p className="text-sm text-yellow-800">
                        Emergency pause is unavailable. Configure{' '}
                        <code className="bg-yellow-100 px-1 rounded">LIMITED_MINTER_PRIVATE_KEY</code>{' '}
                        in the backend environment to enable this feature.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {minterUsage && autoApprovalAvailable !== false && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-3">Backend Minter Usage</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Daily Limit</p>
                      <p className="font-semibold text-gray-900">{minterUsage.dailyLimit / 100} HEP</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Per-Tx Limit</p>
                      <p className="font-semibold text-gray-900">{minterUsage.perTxLimit / 100} HEP</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Used Today</p>
                      <p className="font-semibold text-gray-900">{minterUsage.usedToday / 100} HEP</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Remaining</p>
                      <p className="font-semibold text-green-600">{minterUsage.remainingToday / 100} HEP</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t">
                <button
                  onClick={handleSaveSettings}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {/* Pending Signatures Tab */}
          {activeTab === 'signatures' && (
            <div className="space-y-4">
              {signatureRequests.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">No pending signature requests</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Use the admin CLI to approve manual payouts
                  </p>
                </div>
              ) : (
                signatureRequests.map((request) => (
                  <div key={request.id} className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">
                          {request.user?.fullName || 'Unknown User'}
                        </p>
                        <p className="text-sm text-gray-600">{request.user?.email}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Amount: <span className="font-semibold">{request.amount} HEP</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                          {request.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}

              {signatureRequests.length > 0 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> Run <code className="bg-white px-2 py-1 rounded">npm start watch</code> in
                    the admin-cli directory to approve these requests.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Debug Tab */}
          {activeTab === 'debug' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Settings className="text-gray-600" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Admin Debug Mode</p>
                    <p className="text-sm text-gray-600">Enable verbose logging</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.adminDebugEnabled}
                    onChange={(e) => setSettings({ ...settings, adminDebugEnabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-600"></div>
                </label>
              </div>

              <div className="pt-4 border-t">
                <button
                  onClick={handleSaveSettings}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <div className="space-y-6">
              {!payoutStats ? (
                <div className="text-center py-8">
                  <BarChart2 className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
                  <p className="text-gray-600">Loading stats...</p>
                </div>
              ) : (
                <>
                  {/* Time-period cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Today */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-2">Today</p>
                      <p className="text-3xl font-bold text-gray-900">{payoutStats.today.totalHep.toFixed(2)}</p>
                      <p className="text-sm text-gray-500 mb-3">HEP paid out</p>
                      <p className="text-sm text-gray-700">{payoutStats.today.count} payout{payoutStats.today.count !== 1 ? 's' : ''}</p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {payoutStats.today.auto > 0 && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                            {payoutStats.today.auto} auto
                          </span>
                        )}
                        {payoutStats.today.manual > 0 && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                            {payoutStats.today.manual} manual
                          </span>
                        )}
                        {payoutStats.today.rejected > 0 && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                            {payoutStats.today.rejected} rejected
                          </span>
                        )}
                      </div>
                    </div>

                    {/* This Week */}
                    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                      <p className="text-xs font-semibold text-indigo-500 uppercase tracking-wide mb-2">This Week</p>
                      <p className="text-3xl font-bold text-gray-900">{payoutStats.thisWeek.totalHep.toFixed(2)}</p>
                      <p className="text-sm text-gray-500 mb-3">HEP paid out</p>
                      <p className="text-sm text-gray-700">{payoutStats.thisWeek.count} payout{payoutStats.thisWeek.count !== 1 ? 's' : ''}</p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {payoutStats.thisWeek.auto > 0 && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                            {payoutStats.thisWeek.auto} auto
                          </span>
                        )}
                        {payoutStats.thisWeek.manual > 0 && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                            {payoutStats.thisWeek.manual} manual
                          </span>
                        )}
                      </div>
                    </div>

                    {/* This Month */}
                    <div className="p-4 bg-violet-50 rounded-lg border border-violet-100">
                      <p className="text-xs font-semibold text-violet-500 uppercase tracking-wide mb-2">This Month</p>
                      <p className="text-3xl font-bold text-gray-900">{payoutStats.thisMonth.totalHep.toFixed(2)}</p>
                      <p className="text-sm text-gray-500 mb-3">HEP paid out</p>
                      <p className="text-sm text-gray-700">{payoutStats.thisMonth.count} payout{payoutStats.thisMonth.count !== 1 ? 's' : ''}</p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {payoutStats.thisMonth.auto > 0 && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                            {payoutStats.thisMonth.auto} auto
                          </span>
                        )}
                        {payoutStats.thisMonth.manual > 0 && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                            {payoutStats.thisMonth.manual} manual
                          </span>
                        )}
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                          {payoutStats.thisMonth.uniqueUsers} user{payoutStats.thisMonth.uniqueUsers !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Summary cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* All-time */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">All Time</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total paid out</span>
                          <span className="font-semibold text-gray-900">{payoutStats.allTime.totalHep.toFixed(2)} HEP</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total payouts</span>
                          <span className="font-semibold text-gray-900">{payoutStats.allTime.count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Average payout</span>
                          <span className="font-semibold text-gray-900">{payoutStats.allTime.avgPayoutHep.toFixed(2)} HEP</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Largest payout</span>
                          <span className="font-semibold text-gray-900">{payoutStats.allTime.maxPayoutHep.toFixed(2)} HEP</span>
                        </div>
                      </div>
                    </div>

                    {/* Pending queue */}
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-xs font-semibold text-yellow-600 uppercase tracking-wide mb-3">Pending Queue</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Awaiting approval</span>
                          <span className="font-semibold text-gray-900">{payoutStats.pending.count} request{payoutStats.pending.count !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Value in queue</span>
                          <span className="font-semibold text-yellow-700">{payoutStats.pending.totalHep.toFixed(2)} HEP</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
